import React, { useEffect } from "react";
import Select from "react-select/creatable"; // Using Creatable Select
import { useItemsFilter } from "../../_helper/actions/app/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify"; // For notifications

const BillHeader = ({
  prescription,
  setPrescription,
  isEdit,
  isPublished,
  pharmacy_id,
}) => {
  const isFieldsDisabled = prescription?.mobile?.length !== 10;

  const {
    data: searchPatient,
    isLoading,
    refetch,
  } = useItemsFilter(
    ApiEndPoint.USERS,
    `?action=getSearchPatient&family_id=${prescription?.mobile}`,
    prescription?.mobile?.length === 10
  );

  const { data: linkedDoctor } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getPharmacyLinkedDoctor&pharmacy_id=${pharmacy_id}`
  );

  // Map linkedDoctor data to options for the doctor select
  const doctorOptions =
    linkedDoctor && Array.isArray(linkedDoctor)
      ? linkedDoctor.map((doctor) => ({
          value: doctor.id,
          label: doctor.doctor_name,
        }))
      : [];

  // Helper function to format ISO date strings to YYYY-MM-DD
  const formatISODate = (dateString) => {
    if (!dateString) return "";
    return dateString.includes("T") ? dateString.split("T")[0] : dateString;
  };

  useEffect(() => {
    if (isEdit) {
      refetch();
    }
  }, [isEdit, refetch]);

  useEffect(() => {
    if (searchPatient?.length) {
      const options = searchPatient.map((patient) => ({
        value: patient.id,
        label: patient.full_name,
        age: patient.age,
        gender: patient.gender,
        phone: patient.phone,
        dob: patient.dob,
        user: patient.user,
        is_parent: patient.is_parent,
      }));
      setPrescription((prev) => ({
        ...prev,
        patientOptions: options,
      }));
    }
  }, [searchPatient, setPrescription]);

  // Set default date of billing if not already set
  useEffect(() => {
    if (!prescription.date) {
      setPrescription((prev) => ({
        ...prev,
        date: new Date().toISOString().split("T")[0],
      }));
    }
  }, [prescription.date, setPrescription]);

  console.log("prescription date", prescription.date);

  const calculateAgeFromDOB = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    if (
      today.getMonth() < birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age.toString();
  };

  const calculateDOBFromAge = (age) => {
    if (!age || isNaN(parseInt(age))) return "";
    const birthYear = new Date().getFullYear() - parseInt(age);
    return new Date(birthYear, new Date().getMonth(), new Date().getDate())
      .toISOString()
      .split("T")[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPrescription((prev) => {
      let updatedData = { ...prev, [name]: value };

      if (name === "age") {
        updatedData.dob = calculateDOBFromAge(value);
      } else if (name === "dob") {
        updatedData.age = calculateAgeFromDOB(value);
      }

      return updatedData;
    });
  };

  const handlePatientChange = (selectedOption) => {
    if (selectedOption) {
      setPrescription((prev) => ({
        ...prev,
        id: selectedOption.value,
        full_name: selectedOption.label,
        age: selectedOption.age,
        gender: selectedOption.gender,
        mobile: selectedOption.phone || prev.mobile, // Preserve existing mobile if phone is not available
        dob: selectedOption.dob,
        selectedPatient: selectedOption,
      }));
    } else {
      setPrescription((prev) => ({
        ...prev,
        id: "",
        full_name: "",
        age: "",
        gender: "",
        dob: "",
        selectedPatient: null,
        // Do not reset mobile here
      }));
    }
  };

  const handleCreateNewPatient = async (inputValue) => {
    if (!inputValue.trim()) {
      toast.error("Invalid name. Please enter a valid patient name.");
      return;
    }

    const newPatient = {
      full_name: inputValue,
      age: "",
      gender: "",
      mobile: prescription.mobile || "",
      dob: "",
      value: Date.now(), // Temporary unique ID
      label: inputValue,
      isNewFamilyMember: true,
      isNewPatient: searchPatient.length === 0 ? true : false,
    };

    setPrescription((prev) => ({
      ...prev,
      patientOptions: [...(prev.patientOptions || []), newPatient],
      selectedPatient: newPatient,
      full_name: inputValue,
      age: "",
      gender: "",
      dob: "",
    }));

    toast.success(`Added "${inputValue}" as a new patient.`);
  };

  // New handler for doctor select changes
  const handleDoctorChange = (selectedOption) => {
    if (selectedOption) {
      setPrescription((prev) => ({
        ...prev,
        doctor_id: selectedOption.value,
        doctor_name: selectedOption.label,
        selectedDoctor: selectedOption,
      }));
    } else {
      setPrescription((prev) => ({
        ...prev,
        doctor_id: "",
        doctor_name: "",
        selectedDoctor: null,
      }));
    }
  };

  useEffect(() => {
    if (prescription?.mobile?.length < 10) {
      setPrescription((prev) => ({
        ...prev,
        patientOptions: [],
        selectedPatient: null,
        age: "",
        dob: "",
        gender: "",
        full_name: "",
      }));
    }
  }, [prescription?.mobile, setPrescription]);

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">
        Billing Information
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Mobile No*
          </label>
          <input
            type="text"
            name="mobile"
            value={prescription.mobile || ""}
            readOnly={isEdit || isPublished}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:ring focus:ring-green-300"
            placeholder="Enter Mobile No"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Patient Name*
          </label>
          <Select
            isCreatable
            options={prescription.patientOptions}
            value={prescription.selectedPatient}
            onChange={handlePatientChange}
            onCreateOption={handleCreateNewPatient}
            isClearable
            isSearchable
            isLoading={isLoading}
            placeholder="Search or create a new patient"
            className="w-full"
            classNamePrefix="react-select"
            isDisabled={isFieldsDisabled || isPublished}
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">Age*</label>
          <input
            type="number"
            name="age"
            value={prescription.age || ""}
            onChange={handleChange}
            disabled={isFieldsDisabled || isPublished}
            className="w-full px-4 py-2 border rounded-lg focus:ring focus:ring-green-300"
            placeholder="Enter Age"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Date of Birth*
          </label>
          <input
            type="date"
            name="dob"
            value={formatISODate(prescription.dob)}
            onChange={handleChange}
            disabled={isFieldsDisabled || isPublished}
            className="w-full px-4 py-2 border rounded-lg focus:ring focus:ring-green-300"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Gender*
          </label>
          <select
            name="gender"
            value={prescription.gender || ""}
            onChange={handleChange}
            disabled={isFieldsDisabled || isPublished}
            className="w-full px-4 py-2 border rounded-lg focus:ring focus:ring-green-300"
          >
            <option value="" disabled>
              Select Gender
            </option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {/* Date of Billing Field */}
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Date of Billing*
          </label>
          <input
            type="date"
            name="date"
            value={formatISODate(prescription.date)}
            onChange={handleChange}
            disabled={isFieldsDisabled || isPublished}
            className="w-full px-4 py-2 border rounded-lg focus:ring focus:ring-green-300"
          />
        </div>
        {/* Doctor Select Field */}
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Prescribed By Doctor
          </label>
          <Select
            options={doctorOptions}
            value={prescription.selectedDoctor || null}
            onChange={handleDoctorChange}
            isClearable
            placeholder="Select doctor"
            className="w-full"
            classNamePrefix="react-select"
            isDisabled={isPublished}
          />
        </div>
      </div>
    </div>
  );
};

export default BillHeader;
